import React, { useMemo } from 'react'
import type { TimePickerProps } from './TimePicker.types'
import {
  buildTimePickerDate,
  getTimePickerParts,
  TIME_PICKER_HOURS,
  TIME_PICKER_MINUTES,
  TIME_PICKER_PERIODS,
} from './TimePicker.utils'
import './TimePicker.css'

const TimePicker: React.FC<TimePickerProps> = ({
  value = null,
  onChange,
  darkMode = false,
  className = '',
}) => {
  const selectedParts = useMemo(() => {
    if (value) {
      return getTimePickerParts(value)
    }

    const now = new Date()
    return getTimePickerParts(now)
  }, [value])

  const handleSelect = (
    hours: string,
    minutes: string,
    period: typeof selectedParts.period,
  ): void => {
    onChange?.(buildTimePickerDate(hours, minutes, period, value ?? new Date()))
  }

  return (
    <div
      className={`ds-time-picker${darkMode ? ' ds-time-picker--dark' : ''}${
        className ? ` ${className}` : ''
      }`}
    >
      <div
        className="ds-time-picker__columns"
        role="group"
        aria-label="Select time"
      >
        <div className="ds-time-picker__column" aria-label="Hour">
          {TIME_PICKER_HOURS.map((hour) => (
            <button
              key={hour}
              type="button"
              className={`ds-time-picker__option${
                selectedParts.hours === hour
                  ? ' ds-time-picker__option--selected'
                  : ''
              }`}
              onClick={() =>
                handleSelect(hour, selectedParts.minutes, selectedParts.period)
              }
            >
              {hour}
            </button>
          ))}
        </div>
        <div className="ds-time-picker__column" aria-label="Minute">
          {TIME_PICKER_MINUTES.map((minute) => (
            <button
              key={minute}
              type="button"
              className={`ds-time-picker__option${
                selectedParts.minutes === minute
                  ? ' ds-time-picker__option--selected'
                  : ''
              }`}
              onClick={() =>
                handleSelect(selectedParts.hours, minute, selectedParts.period)
              }
            >
              {minute}
            </button>
          ))}
        </div>
        <div
          className="ds-time-picker__column ds-time-picker__column--period"
          aria-label="Period"
        >
          {TIME_PICKER_PERIODS.map((period) => (
            <button
              key={period}
              type="button"
              className={`ds-time-picker__option${
                selectedParts.period === period
                  ? ' ds-time-picker__option--selected'
                  : ''
              }`}
              onClick={() =>
                handleSelect(selectedParts.hours, selectedParts.minutes, period)
              }
            >
              {period}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default TimePicker
